import { createFileRoute } from "@tanstack/react-router";
import {
	Bot,
	CalendarClock,
	GitBranch,
	Megaphone,
	Sparkles,
	Zap,
} from "lucide-react";
import { FeatureGrid } from "@/components/feature-grid";
import { MarketingHero } from "@/components/marketing-hero";
import { MarketingPageShell } from "@/components/marketing-page-shell";
import { MarketingSection } from "@/components/marketing-section";
import { buildPageHead } from "@/lib/seo-meta";

const path = "/for/founders";

export const Route = createFileRoute(path)({
	component: ForFounders,
	head: ({ matches }) =>
		buildPageHead({
			matches,
			internalPath: path,
			title: "AgentReacher for Founders - AI Social Media for Builders",
			description:
				"Founders ship code, not captions. AgentReacher schedules your launches, build-in-public updates, and threads from chat  -  across X, LinkedIn, and Bluesky.",
		}),
});

function ForFounders() {
	return (
		<MarketingPageShell>
			<MarketingHero
				eyebrow="For founders"
				title="Stay visible while you build."
				description="Drop a PR diff or changelog into chat. Get a launch thread in your voice, scheduled across X, LinkedIn, and Bluesky. Keep your timeline alive on the weeks the work owns you."
			/>

			<MarketingSection
				title="What founders get out of AgentReacher"
				description="The minimum viable social media stack for solo and small-team founders."
			>
				<FeatureGrid
					features={[
						{
							icon: Bot,
							title: "Voice-matched drafts",
							description:
								"The agent learns your tone from past posts. Launches sound like you, not a marketing intern.",
						},
						{
							icon: Zap,
							title: "Built for fast launches",
							description:
								"Drop a changelog or PR diff in chat. Get a launch thread in 30 seconds.",
						},
						{
							icon: GitBranch,
							title: "Bluesky, X, LinkedIn",
							description:
								"The three networks founders actually post on. Cross-post in one click.",
						},
						{
							icon: CalendarClock,
							title: "Build in public on autopilot",
							description:
								"Set a cadence and the agent fills the queue. Skip a week, the agent catches up.",
						},
						{
							icon: Megaphone,
							title: "Launch days made simple",
							description:
								"Pre-built launch templates: announcement, follow-up, thread, founder note.",
						},
						{
							icon: Sparkles,
							title: "Drive from Cursor or Claude",
							description:
								"Post from the same editor you ship from. MCP integration native.",
						},
					]}
				/>
			</MarketingSection>

			<MarketingSection title="A typical founder workflow" align="left">
				<ol className="mx-auto max-w-2xl space-y-3 text-sm leading-relaxed text-muted-foreground">
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							1
						</span>
						<span>
							Connect X, Bluesky, and LinkedIn. Skip the rest until you need
							them.
						</span>
					</li>
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							2
						</span>
						<span>Drop in 10 past posts so the agent learns your voice.</span>
					</li>
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							3
						</span>
						<span>
							Pick a cadence: 'three posts a week, every weekday morning'.
						</span>
					</li>
					<li className="flex gap-4">
						<span className="flex size-6 shrink-0 items-center justify-center rounded-md border border-border text-xs font-medium text-foreground">
							4
						</span>
						<span>
							Approve the calendar weekly. Drop in launch threads from Cursor or
							Claude when needed.
						</span>
					</li>
				</ol>
			</MarketingSection>

			<MarketingSection>
				<div className="mx-auto max-w-2xl space-y-4 text-center text-sm leading-relaxed text-muted-foreground">
					<p>
						Start on Pro and upgrade to Max when you outgrow it. €19/mo covers
						most founders for the first year.
					</p>
				</div>
			</MarketingSection>
		</MarketingPageShell>
	);
}
